import {
  XIcon,
  ArrowLeftIcon,
  CameraIcon,
  ChatCircleDotsIcon,
  WarningIcon,
  BellIcon,
  PlusCircleIcon,
} from "@phosphor-icons/react";
import { useI18N, useFooterSettings } from "common/hooks";
import { isPlaceholderCategoryLabel } from "common/utils";
import { Venue } from "common/types";
import { BusinessTypeIcon } from "@/components/VenueCard/BusinessTypeIcon";
import { AccessibilityInfoIcon } from "@/components/VenueCard/SociabilityIcons";
import VenueCard from "@/components/VenueCard/VenueCard";
import { cn } from "@/lib/utils";
import footerDataFallback from "@/components/Footer/footerData.json";

export type ContributionStep = "intro" | "options" | "unsupported";
export type ContributionVariant = "card" | "sheet" | "page";
export type ContributionAction = "photos" | "experiences" | "alerts";

export const CONTRIBUTION_OPTIONS = [
  { id: "experiences", labelKey: "poiShareExperiences", Icon: ChatCircleDotsIcon },
  { id: "photos", labelKey: "poiAddPhotos", Icon: CameraIcon },
  { id: "alerts", labelKey: "poiReportAlert", Icon: WarningIcon },
] as const;

type Props = {
  step: ContributionStep;
  /** True while the grey venue is being created before navigation. */
  creating: boolean;
  /** Venue/place name, when known. Omitted for unsupported places. */
  name?: string;
  /** Supported-but-not-created POI's business type, shown as the header badge. */
  businessType?: string;
  /** Google's label for an unsupported category, shown as a chip. */
  category?: string;
  /** card = desktop anchored popover, sheet = mobile bottom sheet, page = mobile full-screen */
  variant?: ContributionVariant;
  onGetNotified: () => void;
  onShowOptions: () => void;
  onSelectAction: (action: ContributionAction) => void;
  onClose: () => void;
  onBack?: () => void;

  // When the "intro" place is already a grey venue in our DB (not a brand new
  // Google POI), render the full VenueCard snippet (badge/hours/accessibility
  // line) instead of the plain name+message layout, which has none of that data.
  venue?: Venue;
  onOpenVenue?: (venue: Venue) => void;
  onSaveVenue?: (venue: Venue) => void;
  savedPlaces?: unknown;
};

type Option = (typeof CONTRIBUTION_OPTIONS)[number];

/**
 * Opt-in contribution flow shown when a native Google POI (or grey venue) is
 * tapped. intro (Get notified / Add information) -> options (pick an action)
 * -> navigate to the venue detail page. "unsupported" is terminal.
 */
export function PoiContributionCard({
  step,
  creating,
  name,
  businessType,
  category,
  variant = "card",
  onGetNotified,
  onShowOptions,
  onSelectAction,
  onClose,
  onBack,
  venue,
  onOpenVenue,
  onSaveVenue,
  savedPlaces,
}: Props) {
  const i18n = useI18N();
  const { footerSettings } = useFooterSettings();
  const footerData = footerSettings || footerDataFallback;

  // businessType can be the backend's literal "Venue" placeholder, not a
  // real classification — treat that the same as no businessType at all.
  const knownBusinessType = isPlaceholderCategoryLabel(businessType)
    ? undefined
    : businessType;

  // Same shape as VenueCard's grey-pin map snippet (name, accessibility line,
  // Get alerts / Add info), so a brand new Google POI and a grey venue we
  // already hold read identically on the map.
  const IntroBody = ({ className }: { className?: string }) => (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex flex-col gap-0.5">
        {name && (
          <p className="label-l-bold text-fg-neutral-strong truncate">{name}</p>
        )}
        <div className="flex items-center gap-2 mt-1.5">
          <AccessibilityInfoIcon className="w-4 h-4 shrink-0 text-fg-brand-subtle" />
          <span className="label-m-medium text-fg-neutral-strong">
            {i18n.get("beTheFirstToAddAccessibilityInfo")}
          </span>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onGetNotified();
          }}
          className="flex-1 min-h-10 inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 label-s-medium border border-border-token-brand-minimal text-fg-neutral-strong hover:bg-bg-brand-secondary-hover focus-blue"
        >
          <BellIcon size={16} className="shrink-0" />
          {i18n.get("getAlerts")}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onShowOptions();
          }}
          className="flex-1 min-h-10 inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 label-s-medium bg-bg-brand-primary-default text-white hover:bg-bg-brand-primary-hover focus-blue"
        >
          <PlusCircleIcon size={16} weight="fill" className="shrink-0" />
          {i18n.get("addInfo")}
        </button>
      </div>
    </div>
  );

  // Desktop options list row: icon, label, and a "mobile app only" badge for
  // actions that can't be completed on desktop web (photos/alerts).
  const OptionRow = ({ option: { id, labelKey, Icon } }: { option: Option }) => {
    const isMobileOnly = id !== "experiences";
    return (
      <button
        type="button"
        disabled={creating}
        onClick={(e) => {
          e.stopPropagation();
          if (isMobileOnly) return;
          onSelectAction(id);
        }}
        className={cn(
          "flex items-center gap-3 rounded-xl border p-4 text-left transition-colors focus-blue",
          "border-border-token-neutral-minimal",
          !isMobileOnly && "hover:bg-bg-brand-ghost-hover",
          creating && "opacity-40 cursor-not-allowed",
        )}
      >
        <span className="flex items-center justify-center w-10 h-10 rounded-md bg-bg-brand-ghost-hover shrink-0">
          <Icon size={20} className="text-fg-brand-subtle" />
        </span>
        <span className="flex-1 title-s-semibold whitespace-nowrap">{i18n.get(labelKey)}</span>
        {isMobileOnly && (
          <span className="shrink-0 whitespace-nowrap rounded-full border border-border-token-brand-subtle bg-container-brand-minimal px-2.5 py-1 label-s-medium text-fg-neutral-strong">
            {i18n.get("mobileAppOnly")}
          </span>
        )}
      </button>
    );
  };

  const OptionTile = ({
    option: { id, labelKey, Icon },
    large,
  }: {
    option: Option;
    large: boolean;
  }) => {
    // Same rule as the desktop rows: only sharing an experience works on the
    // web, photos and alerts need the app.
    const isMobileOnly = id !== "experiences";

    return (
      <button
        type="button"
        disabled={creating}
        aria-disabled={isMobileOnly || undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (isMobileOnly) return;
          onSelectAction(id);
        }}
        className={cn(
          "flex flex-col items-start gap-2 rounded-xl border text-left transition-colors focus-blue",
          "border-border-token-neutral-minimal",
          !isMobileOnly && "hover:bg-bg-brand-ghost-hover",
          large ? "p-4 min-h-[120px]" : "p-3",
          creating && "opacity-40 cursor-not-allowed",
        )}
      >
        <span
          className={cn(
            "flex items-center justify-center rounded-md bg-bg-brand-ghost-hover",
            large ? "w-10 h-10" : "w-8 h-8",
          )}
        >
          <Icon size={large ? 22 : 18} className="text-fg-brand-subtle" />
        </span>
        <span className={large ? "title-s-semibold" : "label-m-medium"}>
          {i18n.get(labelKey)}
        </span>
        {isMobileOnly && (
          <span className="mt-auto shrink-0 whitespace-nowrap rounded-full border border-border-token-brand-subtle bg-container-brand-minimal px-2.5 py-1 label-s-medium text-fg-neutral-strong">
            {i18n.get("mobileAppOnly")}
          </span>
        )}
      </button>
    );
  };

  /** Scan-to-get-the-app panel. Shown on every viewport: photos and alerts
   *  can only be finished in the app, so the download route must be reachable. */
  const AppDownloadPanel = ({ className }: { className?: string }) => (
    <div
      className={cn(
        "flex w-full flex-col items-center gap-4 rounded-2xl bg-[#F2F3F4] p-6 text-center",
        className,
      )}
    >
      <p className="label-m-medium text-fg-neutral-medium">
        {i18n.get("scanTheQRCodeToDownloadTheApp")}
      </p>
      <img
        src="/images/Sociability_App_Download_Smart_Link_QR_Code.png"
        alt="QR code to download Sociability app"
        className="h-[140px] w-[140px] rounded-lg"
      />
      <div className="flex items-center justify-center gap-2">
        {footerData.appStoreLinks.map((link) => (
          <a
            key={link.id}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="focus-blue inline-flex h-8 w-[108px] shrink-0 items-center justify-center transition-opacity hover:opacity-80"
            aria-label={i18n.t(link.ariaLabelKey)}
          >
            <img
              src={link.image}
              alt={i18n.t(link.ariaLabelKey)}
              className="h-full w-full object-contain"
            />
          </a>
        ))}
      </div>
    </div>
  );

  // Mobile full-screen "What you can do" page.
  if (variant === "page") {
    return (
      <div className="flex flex-col h-full w-full bg-bg-brand-secondary-default text-fg-neutral-strong">
        <div className="flex items-center px-4 pt-4 pb-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              (onBack ?? onClose)();
            }}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-bg-brand-ghost-hover focus-blue"
            aria-label={i18n.get("back")}
          >
            <ArrowLeftIcon size={22} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4">
          {/* Same single heading as desktop — no separate "What you can do"
              line above it. */}
          <h1 className="title-l-bold mb-5">{i18n.get("poiSelectApply")}</h1>
          <div className="grid grid-cols-2 gap-3">
            {CONTRIBUTION_OPTIONS.map((o) => (
              <OptionTile key={o.id} option={o} large />
            ))}
          </div>
          <AppDownloadPanel className="mt-5 mb-6" />
        </div>
      </div>
    );
  }

  const isSheet = variant === "sheet";

  // "intro" for a venue we already hold (grey pin, no accessibility data yet):
  // reuse VenueCard's own grey-header snippet so it shows the real
  // badge/distance/hours/accessibility line instead of a generic message.
  // Brand new Google POIs (no venue yet) fall through to the plain card below.
  if (step === "intro" && venue) {
    return (
      <div className={cn(isSheet ? "w-full" : "w-[320px] max-w-[80vw]")}>
        <VenueCard
          location={venue}
          onOpen={() => onOpenVenue?.(venue)}
          onSaveVenue={onSaveVenue}
          savedPlaces={savedPlaces}
          onClose={onClose}
          onNotificationClick={onGetNotified}
          truncateText
          showMetaInfo
          showOpeningHours
          showVariantIndicators
          showActionButtons
          showAddress={false}
          inMapView
        />
      </div>
    );
  }

  // Desktop "unsupported category" card: grey header (badge + close), white
  // body — a different shape from intro/options, which stay one flat card.
  if (step === "unsupported" && !isSheet) {
    return (
      <div className="relative w-[320px] max-w-[80vw] rounded-xl shadow-xl overflow-hidden text-fg-neutral-strong bg-bg-brand-secondary-default">
        <div className="bg-[#F2F3F4] px-3 py-3 flex items-center justify-between gap-2">
          {category ? (
            <div className="inline-flex items-center gap-1 min-w-0 rounded-3xl border border-border-token-neutral-minimal bg-bg-brand-secondary-default px-3 py-1.5">
              <BusinessTypeIcon
                businessType={category}
                className="w-4 h-4 text-fg-neutral-strong shrink-0"
              />
              <span className="label-s-medium text-fg-neutral-strong truncate">
                {category}
              </span>
            </div>
          ) : (
            <span />
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="shrink-0 flex items-center justify-center w-8 h-8 bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue"
            aria-label={i18n.get("close")}
            type="button"
          >
            <XIcon size={16} weight="bold" />
          </button>
        </div>
        <div className="p-4">
          <h3 className="title-s-semibold text-fg-neutral-strong mb-1">
            {i18n.get("poiUnsupportedCardTitle")}
          </h3>
          <p className="body-m-medium text-fg-neutral-medium">
            {i18n.get("poiUnsupportedSubtitle")}
          </p>
        </div>
      </div>
    );
  }

  // Desktop "intro" for a brand new POI (supported type, nothing created yet):
  // same grey header + white body shape as unsupported, but with the actual
  // name/CTAs. No venue yet, so no heart button and no click-to-open body.
  if (step === "intro" && !venue && !isSheet) {
    return (
      <div className="relative w-[320px] max-w-[80vw] rounded-xl shadow-xl overflow-hidden text-fg-neutral-strong bg-bg-brand-secondary-default">
        <div className="bg-[#F2F3F4] px-3 py-3 flex items-center justify-between gap-2">
          {knownBusinessType ? (
            <div className="inline-flex items-center gap-1 min-w-0 rounded-3xl border border-border-token-neutral-minimal bg-bg-brand-secondary-default px-3 py-1.5">
              <BusinessTypeIcon
                businessType={knownBusinessType}
                className="w-4 h-4 text-fg-neutral-strong shrink-0"
              />
              <span className="label-s-medium text-fg-neutral-strong truncate">
                {knownBusinessType}
              </span>
            </div>
          ) : (
            <span />
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="shrink-0 flex items-center justify-center w-8 h-8 bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue"
            aria-label={i18n.get("close")}
            type="button"
          >
            <XIcon size={16} weight="bold" />
          </button>
        </div>
        <IntroBody className="px-4 pt-3 pb-4" />
      </div>
    );
  }

  // Desktop "options" modal: header bar across the top, then a two-pane body
  // — the action list on the left, the QR/app-store panel always visible on
  // the right (no more reveal-on-click), so desktop users always see how to
  // finish photos/alerts in the app.
  if (step === "options" && !isSheet) {
    return (
      <div className="relative flex w-[840px] max-w-[92vw] flex-col overflow-hidden rounded-2xl bg-bg-brand-secondary-default text-fg-neutral-strong shadow-xl">
        {/* No title bar — the body's own "Choose how you'd like to help"
            heading is the header. Close sits in the top-left corner. */}
        <div className="flex items-center border-b border-border-token-neutral-minimal px-4 py-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-bg-brand-ghost-hover focus-blue"
            aria-label={i18n.get("close")}
            type="button"
          >
            <XIcon size={18} weight="bold" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="flex-1 min-w-0 py-6 pl-6 pr-3">
            <h3 className="title-l-bold mb-4">
              {i18n.get("poiSelectApply")}
            </h3>
            <div className="flex flex-col gap-3">
              {CONTRIBUTION_OPTIONS.map((o) => (
                <OptionRow key={o.id} option={o} />
              ))}
            </div>
          </div>

          <div className="hidden md:flex w-[320px] shrink-0 items-center py-6 pl-2 pr-6">
            <AppDownloadPanel />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative text-fg-neutral-strong",
        step === "unsupported"
          ? "bg-[#F2F3F4]"
          : "bg-bg-brand-secondary-default",
        isSheet
          ? "rounded-2xl p-5 w-full shadow-xl"
          : "rounded-xl p-4 w-[320px] max-w-[80vw]",
      )}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center rounded-full bg-bg-brand-secondary-default border border-border-token-neutral-minimal shadow-md hover:bg-bg-brand-ghost-hover focus-blue"
        aria-label={i18n.get("close")}
        type="button"
      >
        <XIcon size={18} weight="bold" />
      </button>

      {step === "intro" && <IntroBody />}

      {step === "unsupported" && (
        <div>
          {category && (
            <div className="mb-3 inline-flex items-center gap-1 rounded-3xl border border-border-token-neutral-minimal bg-bg-brand-tertiary-default px-3 py-1">
              <BusinessTypeIcon
                businessType={category}
                className="w-4 h-4 text-fg-neutral-strong"
              />
              <span className="label-s-medium text-fg-neutral-strong">
                {category}
              </span>
            </div>
          )}
          <h3 className="title-s-semibold text-fg-neutral-strong mb-1">
            {i18n.get("poiUnsupportedCardTitle")}
          </h3>
          <p className="body-m-medium text-fg-neutral-medium">
            {i18n.get("poiUnsupportedSubtitle")}
          </p>
        </div>
      )}
    </div>
  );
}
